import { Link } from '@tanstack/react-router'
import { Seo } from '@saas-pages/shared/public/seo'
import { useLocale, useT } from '@/shared/i18n'
import { seoFor } from '@/seo/routes'
import { buildUtilities } from '@/shared/utilities/utilities'
import { ToolBreadcrumb } from '@/shared/seo/ToolBreadcrumb'
import { ToolFaq } from '@/shared/seo/ToolFaq'

const UMBRELLA_ORDER = [
  'excelVPdf',
  'htmlToPdf',
  'jpgVPdf',
  'png-v-pdf',
  'heicVPdf',
  'vJpg',
  'pdf-to-png',
  'pdfToText',
  'szhat',
  'povernut',
] as const

export function PdfKonverterPage() {
  const t = useT()
  const { locale } = useLocale()
  const allUtilities = buildUtilities(t)
  const utilityMap = Object.fromEntries(allUtilities.map((u) => [u.key, u]))

  const orderedUtilities = UMBRELLA_ORDER
    .map((key) => utilityMap[key])
    .filter((u): u is NonNullable<typeof u> => u != null)

  const route = seoFor('pdfKonverter', locale)

  return (
    <>
      <Seo {...route} />

      <div className="mx-auto max-w-3xl">
        <ToolBreadcrumb
          items={[
            { label: 'pdfy.ru', to: '/' },
            { label: t.umbrella.breadcrumb },
          ]}
        />

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {t.umbrella.h1}
        </h1>
        <p className="mt-4 text-muted-foreground">
          {t.umbrella.sub}
        </p>

        <section
          aria-label={t.umbrella.toolsHeading}
          className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {orderedUtilities.map((u) => (
            <Link
              key={u.key}
              to={u.path}
              className="group block rounded-xl border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl" aria-hidden>
                  {u.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-base font-semibold tracking-tight group-hover:text-primary">
                    {u.title}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {u.shortDescription}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </section>

        <section className="prose prose-slate mx-auto mt-14 max-w-none text-base text-muted-foreground">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            {t.umbrella.longFormHeading}
          </h2>
          <p className="mt-4">{t.umbrella.para1}</p>
          <p className="mt-4">{t.umbrella.para2}</p>
          <p className="mt-4">{t.umbrella.para3}</p>
        </section>

        <ToolFaq items={t.umbrella.faq} heading={t.umbrella.faqHeading} />

        <aside
          data-rsya-slot="footer-banner"
          aria-hidden="true"
          className="mt-12 min-h-[120px]"
        />
      </div>
    </>
  )
}
